export const MONDIALI_HOME_PATH = '/mondiali/home';
export const MONDIALI_ROSTERS_PATH = '/mondiali/rose';
export const MONDIALI_PREMI_PATH = '/mondiali/premi';
export const MONDIALI_TOP_PLAYER_PATH = '/mondiali/top-player';
export const MONDIALI_FINALE_PATH = '/mondiali/finale';

export const getMondialiWeekPath = (weekNumber) => `/mondiali/classifica/${weekNumber}`;

const COMPETITION_YEAR = 2026;

const toUtcTimestamp = (monthIndex, day, hour = 0, minute = 0, second = 0) =>
  Date.UTC(COMPETITION_YEAR, monthIndex, day, hour, minute, second);

const MONDIALI_WARMUP_START_TIMESTAMP = toUtcTimestamp(5, 11, 0, 0, 0);

const MONDIALI_WEEK_WINDOWS = [
  {
    weekNumber: 1,
    startTimestamp: toUtcTimestamp(5, 14, 0, 0, 0),
    endTimestamp: toUtcTimestamp(5, 20, 23, 59, 59),
  },
  {
    weekNumber: 2,
    startTimestamp: toUtcTimestamp(5, 21, 0, 0, 0),
    endTimestamp: toUtcTimestamp(5, 27, 23, 59, 59),
  },
  {
    weekNumber: 3,
    startTimestamp: toUtcTimestamp(5, 28, 0, 0, 0),
    endTimestamp: toUtcTimestamp(6, 4, 23, 59, 59),
  },
  {
    weekNumber: 4,
    startTimestamp: toUtcTimestamp(6, 5, 0, 0, 0),
    endTimestamp: toUtcTimestamp(6, 11, 23, 59, 59),
  },
  {
    weekNumber: 5,
    startTimestamp: toUtcTimestamp(6, 12, 0, 0, 0),
    endTimestamp: toUtcTimestamp(6, 18, 23, 59, 59),
  },
];

const FINALE_START_TIMESTAMP = toUtcTimestamp(6, 19, 0, 0, 0);
const MONDIALI_ARCHIVE_OPEN_TIMESTAMP = toUtcTimestamp(7, 1, 0, 0, 0);
const TOP_PLAYER_START_TIMESTAMP = MONDIALI_WEEK_WINDOWS[0].startTimestamp;

const normalizeTimestamp = (now = new Date()) => {
  if (now instanceof Date) {
    return now.getTime();
  }

  if (typeof now === 'number') {
    return now;
  }

  return new Date(now).getTime();
};

export const getMondialiWeekWindows = () => {
  return MONDIALI_WEEK_WINDOWS.map((window) => ({ ...window }));
};

export const isMondialiWeekNumberValid = (weekNumber) => Number.isInteger(weekNumber) && weekNumber >= 1 && weekNumber <= 5;
export const isMondialiArchiveOpen = (now = new Date()) => normalizeTimestamp(now) >= MONDIALI_ARCHIVE_OPEN_TIMESTAMP;

export const isMondialiWeekAvailable = (weekNumber, now = new Date()) => {
  if (!isMondialiWeekNumberValid(weekNumber)) {
    return false;
  }

  if (isMondialiArchiveOpen(now)) {
    return true;
  }

  const currentTimestamp = normalizeTimestamp(now);
  const selectedWeek = MONDIALI_WEEK_WINDOWS.find((window) => window.weekNumber === weekNumber);
  if (!selectedWeek) {
    return false;
  }

  return currentTimestamp >= selectedWeek.startTimestamp;
};

export const isMondialiFinaleAvailable = (now = new Date()) => normalizeTimestamp(now) >= FINALE_START_TIMESTAMP;
export const isMondialiTopPlayerAvailable = (now = new Date()) => normalizeTimestamp(now) >= TOP_PLAYER_START_TIMESTAMP;

export const getMondialiCompetitionSummaryStage = (now = new Date()) => {
  const currentTimestamp = normalizeTimestamp(now);

  if (currentTimestamp < MONDIALI_WARMUP_START_TIMESTAMP) {
    return 'pre_opening';
  }

  if (currentTimestamp < MONDIALI_WEEK_WINDOWS[0].startTimestamp) {
    return 'warmup';
  }

  if (currentTimestamp < FINALE_START_TIMESTAMP) {
    return 'live';
  }

  return 'final';
};

export const getMondialiCurrentTargetPath = (now = new Date()) => {
  const currentTimestamp = normalizeTimestamp(now);
  const matchingWeek = MONDIALI_WEEK_WINDOWS.find(
    (window) => currentTimestamp >= window.startTimestamp && currentTimestamp <= window.endTimestamp,
  );

  if (matchingWeek) {
    return getMondialiWeekPath(matchingWeek.weekNumber);
  }

  if (currentTimestamp >= FINALE_START_TIMESTAMP) {
    return MONDIALI_FINALE_PATH;
  }

  return MONDIALI_ROSTERS_PATH;
};

export const resolveMondialiHomeRedirectPath = (now = new Date()) => getMondialiCurrentTargetPath(now);

export const resolveMondialiWeekRedirectPath = (weekNumber, now = new Date()) => {
  if (!isMondialiWeekNumberValid(weekNumber)) {
    return getMondialiCurrentTargetPath(now);
  }

  if (isMondialiArchiveOpen(now)) {
    return null;
  }

  if (!isMondialiWeekAvailable(weekNumber, now)) {
    return getMondialiCurrentTargetPath(now);
  }

  return null;
};

export const resolveMondialiFinaleRedirectPath = (now = new Date()) => {
  if (isMondialiArchiveOpen(now)) {
    return null;
  }

  if (!isMondialiFinaleAvailable(now)) {
    return getMondialiCurrentTargetPath(now);
  }

  return null;
};

export const resolveMondialiTopPlayerRedirectPath = (now = new Date()) => {
  if (isMondialiArchiveOpen(now)) {
    return null;
  }

  if (!isMondialiTopPlayerAvailable(now)) {
    return getMondialiCurrentTargetPath(now);
  }

  return null;
};

const formatUtcDateLabel = (timestamp) => {
  const formatter = new Intl.DateTimeFormat('it-IT', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'long',
  });

  return formatter.format(new Date(timestamp));
};

export const getMondialiWeekLabel = (weekNumber) => `${weekNumber}\u00B0 Settimana`;

export const getMondialiWeekRangeLabel = (weekNumber) => {
  if (!isMondialiWeekNumberValid(weekNumber)) {
    return '';
  }

  const selectedWeek = MONDIALI_WEEK_WINDOWS.find((window) => window.weekNumber === weekNumber);
  if (!selectedWeek) {
    return '';
  }

  return `${formatUtcDateLabel(selectedWeek.startTimestamp)} - ${formatUtcDateLabel(selectedWeek.endTimestamp)} UTC`;
};

export const getMondialiSectionNavItems = (now = new Date()) => {
  const currentTimestamp = normalizeTimestamp(now);
  const archiveOpen = isMondialiArchiveOpen(now);
  const weekItems = MONDIALI_WEEK_WINDOWS.map((window) => ({
    key: `week-${window.weekNumber}`,
    label: getMondialiWeekLabel(window.weekNumber),
    path: getMondialiWeekPath(window.weekNumber),
    enabled: archiveOpen || currentTimestamp >= window.startTimestamp,
  }));

  return [
    {
      key: 'rose',
      label: 'Rose',
      path: MONDIALI_ROSTERS_PATH,
      enabled: true,
    },
    {
      key: 'premi',
      label: 'Premi',
      path: MONDIALI_PREMI_PATH,
      enabled: true,
    },
    {
      key: 'top-player',
      label: 'Top Player',
      path: MONDIALI_TOP_PLAYER_PATH,
      enabled: archiveOpen || currentTimestamp >= TOP_PLAYER_START_TIMESTAMP,
    },
    ...weekItems,
    {
      key: 'finale',
      label: 'Finale',
      path: MONDIALI_FINALE_PATH,
      enabled: archiveOpen || currentTimestamp >= FINALE_START_TIMESTAMP,
    },
  ];
};
